<template>
    <DModal
        :title="$t('certificates.create_modal_title')"
        :show="showModal"
        @close="$emit('close-create-modal')"
    >
        <DCard
            padding
        >
            <DFormRow
                :label="$t('certificates.create_mode_label')"
                is-required
            >
                <DRadioGroup
                    id="certificateMode"
                    v-model="form.mode"
                    :options="[
                        { value: CertificateMode.MULTI_SAN, label: $t('certificates.create_mode_multi_san') },
                        { value: CertificateMode.BULK, label: $t('certificates.create_mode_bulk') },
                    ]"
                />
            </DFormRow>
            <DFormRow
                :label="form.mode === CertificateMode.MULTI_SAN ? $t('certificates.create_domains_label_multi_san') : $t('certificates.create_domains_label_bulk')"
                is-required
                :class="domainsFormLevelError === null ? '' : 'mb-0'"
            >
                <DomainListInput
                    ref="domainList"
                    v-model="form.domains"
                    :errors="domainItemErrors"
                    :row-placeholder="domainPlaceholder"
                    :add-placeholder="$t('certificates.create_add_placeholder')"
                    :add-label="$t('common.add')"
                />
            </DFormRow>
            <DFormRow
                v-if="domainsFormLevelError !== null"
                label=""
                class="mb-0"
            >
                <p class="text-danger">
                    {{ domainsFormLevelError }}
                </p>
            </DFormRow>
            <DFormRow
                v-if="errorMessages !== null"
                label=""
                class="mb-0"
            >
                <p
                    class="text-danger"
                >
                    {{ errorMessages }}
                </p>
            </DFormRow>
            <DFormRow
                :label="$t('certificates.create_authority_label')"
                is-required
            >
                <DSelect
                    v-model="form.sslType"
                    :options="sslTypes"
                    add-input-classes="bg-light"
                    :placeholder="$t('certificates.create_select_ssl_type')"
                    :is-valid="isValid('sslType')"
                    :feedback-invalid="errors.sslType"
                    @change="getCertificateTypes"
                />
            </DFormRow>
            <DFormRow
                :label="$t('certificates.create_cert_type_label')"
                is-required
            >
                <DSelect
                    v-model="form.cert_type"
                    :options="availableCertTypes"
                    add-input-classes="bg-light"
                    :placeholder="$t('certificates.create_select_cert_type')"
                    :is-valid="isValid('cert_type')"
                    :feedback-invalid="errors.cert_type"
                    :disabled="form.sslType === null"
                    @change="handleCertTypeChange"
                />
            </DFormRow>
            <DFormRow
                :label="$t('certificates.create_auto_renew_label')"
                is-required
            >
                <DFormCheck
                    v-model="form.autoRenew"
                />
            </DFormRow>
            <DFormRow
                v-if="form.autoRenew"
                label=""
            >
                <DRadioGroup
                    id="duplicatesHandling"
                    v-model="form.duplicatesHandling"
                    :options="[
                        { value: 'skip-duplicates', label: $t('certificates.duplicate_handling_skip')},
                        { value: 'overwrite', label: $t('certificates.duplicate_handling_overwrite')},
                    ]"
                />
            </DFormRow>
            <DFormRow
                :label="$t('certificates.create_custom_subject_label')"
            >
                <DFormCheck
                    v-model="form.customSubject"
                    :disabled="[CertType.EV, CertType.OV, CertType.OV_WILDCARD].includes(form.cert_type)"
                />
            </DFormRow>
            <DFormRow
                v-if="form.customSubject"
                :label="$t('certificates.subject_organization')"
                is-required
                :error="errors.organization"
            >
                <DInput
                    v-model="form.organization"
                />
            </DFormRow>
            <DFormRow
                v-if="form.customSubject"
                :label="$t('certificates.subject_department')"
                is-required
                :error="errors.department"
            >
                <DInput
                    v-model="form.department"
                />
            </DFormRow>
            <DFormRow
                v-if="form.customSubject"
                :label="$t('certificates.subject_country')"
                is-required
                :error="errors.country"
            >
                <DInput
                    v-model="form.country"
                />
            </DFormRow>
            <DFormRow
                v-if="form.customSubject"
                :label="$t('certificates.subject_state')"
                is-required
                :error="errors.state"
            >
                <DInput
                    v-model="form.state"
                />
            </DFormRow>
            <DFormRow
                v-if="form.customSubject"
                :label="$t('certificates.subject_location')"
                is-required
                :error="errors.location"
            >
                <DInput
                    v-model="form.location"
                />
            </DFormRow>
            <DFormRow
                v-if="form.customSubject"
                :label="$t('certificates.subject_email')"
                is-required
                :error="errors.email"
            >
                <DInput
                    v-model="form.email"
                />
            </DFormRow>
            <DFormRow
                v-if="form.customSubject"
                :label="$t('certificates.create_label_phone')"
                :error="errors.phone"
            >
                <DInput
                    v-model="form.phone"
                />
            </DFormRow>
            <DFormRow
                v-if="form.customSubject"
                :label="$t('certificates.subject_address')"
                is-required
                :error="errors.address"
            >
                <DInput
                    v-model="form.address"
                />
            </DFormRow>
            <DFormRow
                v-if="form.customSubject"
                :label="$t('certificates.subject_postal_code')"
                is-required
                :error="errors.postal_code"
            >
                <DInput
                    v-model="form.postal_code"
                />
            </DFormRow>
        </DCard>
        <template #buttons>
            <DButton
                :label="$t('certificates.create_submit_button')"
                color="primary"
                @click="submitForm"
            />
        </template>
    </DModal>
</template>

<script>
import axios from 'axios'
import { usePage } from '@inertiajs/vue3'
import DomainListInput from '../DomainListInput.vue'
import { CertType, CertificateMode, WILDCARD_CERT_TYPES } from '~@/Constants/certificates'

export default {
    name: 'CreateCertificateModal',
    components: { DomainListInput },
    props: {
        sslTypes: {
            type: Array,
            default: () => {
                return []
            }
        },
        certificateForm: {
            type: Object,
            default: () => {
                return {}
            }
        },
        errors: {
            type: Object,
            default: () => {
                return {}
            }
        },
        showModal: {
            type: Boolean,
            default: false
        }
    },
    emits: ['form-submitted', 'close-create-modal'],
    data () {
        return {
            form: this.certificateForm,
            errorMessages: null,
            certTypes: [],
            CertType,
            CertificateMode
        }
    },
    computed: {
        domainsFormLevelError () {
            return this.errors.domains ?? null
        },
        /**
         * In Multi-SAN mode only DV, OV and EV are selectable — wildcard
         * cert types are filtered out of the dropdown.
         */
        availableCertTypes () {
            if (this.form.mode !== CertificateMode.MULTI_SAN) {
                return this.certTypes
            }

            return this.certTypes.filter(option => !WILDCARD_CERT_TYPES.includes(option.value))
        },
        /**
         * Translate the Laravel-shaped `domains.N` validation errors
         * into the per-index map the shared list editor consumes.
         */
        domainItemErrors () {
            const result = {}
            for (let i = 0; i < this.form.domains.length; i++) {
                const key = `domains.${i}`
                if (typeof this.errors[key] !== 'undefined') {
                    result[i] = this.errors[key]
                }
            }
            return result
        }
    },
    watch: {
        /**
         * Switching to Multi-SAN must drop a previously chosen wildcard
         * cert type so the dropdown never holds a now-hidden value.
         */
        'form.mode' (mode) {
            if (mode === CertificateMode.MULTI_SAN && WILDCARD_CERT_TYPES.includes(this.form.cert_type)) {
                this.form.cert_type = null
            }
        }
    },
    updated () {
        this.errorMessages = usePage().props.flash.errorMessages
    },
    methods: {
        submitForm () {
            // Flush whatever the user typed into the add input without
            // hitting Enter — keeps the previous behaviour where
            // pressing "Bestellen" with text in the field still
            // includes it in the submit.
            this.$refs.domainList?.flushPendingInput()
            this.$emit('form-submitted', this.form)
        },
        isValid (field) {
            if (typeof this.errors[field] !== 'undefined') {
                return false
            }

            if (this.form[field] === null) {
                return null
            }

            return null
        },
        domainPlaceholder (index) {
            if (this.form.mode === CertificateMode.MULTI_SAN && index === 0) {
                return this.$t('certificates.create_common_name_placeholder')
            }
            return this.$t('certificate_domains.domain_row_placeholder')
        },
        async getCertificateTypes () {
            try {
                const response = await axios.get(this.$route('certificates.certificate-types', {
                    sslType: this.form.sslType
                }))

                this.certTypes = response.data.map(option => {
                    return { value: option, label: option }
                })
            } catch (e) {
                this.certTypes = []
            }
        },
        handleCertTypeChange () {
            this.form.customSubject = ![CertType.DV, CertType.DV_WILDCARD].includes(this.form.cert_type)
        }
    }
}
</script>

<style scoped>

</style>
